/**
 * The Drafting detail page's binding of the shared detail-page surface.
 *
 * Everything structural — the shell, the drag, the rail, the persistence —
 * lives in `shared/surface`. This file is the whole of what drafting adds:
 * its config (`./layout`) and one typed hook over the generic one.
 *
 * It binds the SELECTION-FREE hook: the page has no detail pane and no reader
 * pane (the canvas IS the document), so it neither names a selection kind nor
 * publishes a `select`/`openReader` that would open a pane it never declared.
 */

import {
  useSurfaceWithoutSelection,
  UseDetailSurfaceOptions,
  UseSurfaceWithoutSelectionResult,
} from '../../shared/surface/useDetailSurface';
import {
  DRAFTING_SURFACE,
  DRAFTING_TABS,
  DraftingPanelId,
  DraftingTab,
} from './layout';

export { DRAFTING_SURFACE, DRAFTING_TABS };
export type { DraftingPanelId, DraftingTab };

export type DraftingWorkspace = UseSurfaceWithoutSelectionResult<
  DraftingPanelId,
  never,
  DraftingTab
>;

/**
 * The Drafting detail page's layout and reading-mode state.
 *
 * Takes the document's uuid: the surface is entity-scoped, so each document
 * remembers its own arrangement — see the note at the top of `./layout`.
 */
export const useDraftingWorkspace = (
  options: UseDetailSurfaceOptions<DraftingTab> = {}
): DraftingWorkspace =>
  useSurfaceWithoutSelection<DraftingPanelId, never, DraftingTab>(
    DRAFTING_SURFACE,
    options
  );
